/* Reusable profile-editing pieces shared by the unified people page
 * (src/components/directories.jsx) and the self profile sections.
 *
 * The old UserProfileModal was retired — profile viewing + editing now
 * lives entirely on the full-page people view. What remains here are the
 * self/admin-aware building blocks it used:
 *   - StaffingSection      — read-only list + inline add/edit for editors
 *   - ModalPhotoUploader   — self/admin photo upload + remove
 */
import React from 'react';
import { Avatar, TaxonomyMultiSelect, fmtPct } from './primitives.jsx';
import { useAutoSave, SaveStatus } from '../lib/use-auto-save.jsx';
import { MarkdownView } from './orgrepo-news-reports-arch.jsx';
import { getUserProfile, patchMyProfile, patchAdminUser, uploadMyPhoto, uploadAdminUserPhoto, deleteAdminUserPhoto, generateMyIntro, generateAdminUserIntro, listTaxonomies, putAdminUserTaxonomies, getUserStaffing, upsertAdminUserStaffing, deleteAdminUserStaffing, upsertMyStaffing, deleteMyStaffing, listProjects } from '../api.js';

// Order-insensitive comparison for two arrays of ids.
const sameSet = (a, b) => a.length === b.length && a.every(x => b.includes(x));

/** Read-only-for-everyone staffing list. Editors get an inline form to
 *  add/update entries and a delete button per row. `self` routes writes
 *  through /api/me/staffing (the signed-in user editing their own
 *  profile); `viewerIsAdmin` routes through the admin endpoints. */
export const StaffingSection = ({ userId, viewerIsAdmin, self = false, title = 'Staffing over time' }) => {
  const canEdit = viewerIsAdmin || self;
  const [state, setState] = React.useState({ loading: true, entries: [], error: null });
  const [projects, setProjects] = React.useState([]);
  const [showForm, setShowForm] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  // Form state used by the admin "add entry" form.
  const [form, setForm] = React.useState(() => ({
    isoWeek: currentIsoWeek(),
    projectType: 'client-project',
    projectId: '',
    percentage: '',
    note: '',
  }));

  const reload = React.useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const { entries } = await getUserStaffing(userId);
      setState({ loading: false, entries, error: null });
    } catch (err) {
      setState({ loading: false, entries: [], error: err.message });
    }
  }, [userId]);

  React.useEffect(() => { reload(); }, [reload]);

  // The form needs the project catalogue for the dropdown. Load on
  // demand — most viewers never open the form.
  React.useEffect(() => {
    if (!canEdit || !showForm || projects.length) return;
    listProjects().then(setProjects).catch(() => setProjects([]));
  }, [canEdit, showForm, projects.length]);

  const submitForm = async e => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        isoWeek: form.isoWeek.trim(),
        projectType: form.projectType,
        projectId: form.projectType === 'client-project' ? (form.projectId || null) : null,
        percentage: Number(form.percentage),
        note: form.note.trim() || null,
      };
      if (self) await upsertMyStaffing(payload);
      else await upsertAdminUserStaffing(userId, payload);
      setForm(f => ({ ...f, percentage: '', note: '' }));
      await reload();
    } catch (err) {
      setState(s => ({ ...s, error: err.message }));
    } finally {
      setBusy(false);
    }
  };

  const removeEntry = async id => {
    if (!confirm('Delete this staffing entry?')) return;
    try {
      if (self) await deleteMyStaffing(id);
      else await deleteAdminUserStaffing(userId, id);
      await reload();
    } catch (err) {
      setState(s => ({ ...s, error: err.message }));
    }
  };

  const byWeek = groupByWeek(state.entries);
  const currentWeek = currentIsoWeek();

  return (
    <div style={{marginTop:24,paddingTop:18,borderTop:'1px solid var(--border)'}}>
      <div style={{display:'flex',alignItems:'baseline',justifyContent:'space-between',marginBottom:8}}>
        <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600}}>
          {title}
        </div>
        {canEdit && (
          <button
            type="button"
            className="btn-ghost"
            style={{padding:'3px 10px',fontSize:11.5}}
            onClick={() => setShowForm(v => !v)}
          >
            {showForm ? 'Close' : '+ Add entry'}
          </button>
        )}
      </div>

      {state.error && (
        <div style={{padding:10,marginBottom:10,background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:12.5}}>
          {state.error}
        </div>
      )}

      {canEdit && showForm && (
        <form onSubmit={submitForm} style={{
          padding:12,marginBottom:12,background:'var(--bg-alt)',borderRadius:8,
          display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:8,alignItems:'end',
        }}>
          <div className="field" style={{margin:0}}>
            <label style={{fontSize:11}}>ISO week</label>
            <input
              className="input"
              value={form.isoWeek}
              onChange={e => setForm({ ...form, isoWeek: e.target.value })}
              placeholder="2026-W21"
              maxLength={8}
            />
          </div>
          <div className="field" style={{margin:0}}>
            <label style={{fontSize:11}}>Type</label>
            <select
              className="select"
              value={form.projectType}
              onChange={e => setForm({ ...form, projectType: e.target.value, projectId: '' })}
            >
              <option value="client-project">Client project</option>
              <option value="internal">Internal</option>
              <option value="vacation">Vacation</option>
              <option value="public-holiday">Public holiday</option>
              <option value="sick">Sick</option>
            </select>
          </div>
          {form.projectType === 'client-project' && (
            <div className="field" style={{margin:0,gridColumn:'span 2'}}>
              <label style={{fontSize:11}}>Project</label>
              <select
                className="select"
                value={form.projectId}
                onChange={e => setForm({ ...form, projectId: e.target.value })}
              >
                <option value="">— pick a project —</option>
                {projects.map(p => (
                  <option key={p._id || p.id} value={p._id || p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="field" style={{margin:0}}>
            <label style={{fontSize:11}}>%</label>
            <input
              className="input"
              type="number"
              min={0}
              max={100}
              value={form.percentage}
              onChange={e => setForm({ ...form, percentage: e.target.value })}
              placeholder="0–100"
            />
          </div>
          <div className="field" style={{margin:0,gridColumn:'span 2'}}>
            <label style={{fontSize:11}}>Note (optional)</label>
            <input
              className="input"
              value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value })}
              maxLength={500}
            />
          </div>
          <div style={{display:'flex',justifyContent:'flex-end'}}>
            <button
              type="submit"
              className="btn-orange"
              disabled={busy || !form.isoWeek || form.percentage === '' || (form.projectType === 'client-project' && !form.projectId)}
              style={{padding:'6px 14px',fontSize:12.5}}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      )}

      {state.loading ? (
        <div style={{padding:16,color:'var(--fg-muted)',fontSize:12.5}}>Loading staffing…</div>
      ) : byWeek.length === 0 ? (
        <div style={{padding:16,color:'var(--fg-muted)',fontSize:12.5,background:'var(--bg-alt)',borderRadius:8}}>
          {self
            ? 'No projects yet — use “+ Add entry” to log the projects you’re working on.'
            : canEdit
              ? 'No staffing entries yet — use “+ Add entry” to record allocations.'
              : 'No staffing entries on file yet for this person.'}
        </div>
      ) : (
        <div style={{display:'grid',gap:10}}>
          {byWeek.map(week => (
            <WeekBlock
              key={week.isoWeek}
              week={week}
              isCurrent={week.isoWeek === currentWeek}
              canEdit={canEdit}
              onDelete={removeEntry}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const WeekBlock = ({ week, isCurrent, canEdit, onDelete }) => {
  const total = week.entries.reduce((s, e) => s + (e.percentage || 0), 0);
  const under = total < 80;
  return (
    <div style={{
      border: isCurrent ? '1px solid var(--orange)' : '1px solid var(--border)',
      borderRadius:8, padding:'10px 12px', background:'var(--bg-card)',
    }}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
        <div style={{fontSize:12.5,fontWeight:600}}>
          {week.isoWeek} {isCurrent && <span style={{fontSize:10.5,color:'var(--orange)',marginLeft:6}}>· this week</span>}
        </div>
        <div style={{fontSize:11.5,color: under ? 'var(--orange)' : 'var(--fg-muted)'}}>
          {under && <span title="Under 80% allocation" style={{marginRight:4}}>!</span>}
          {fmtPct(total)}% allocated
        </div>
      </div>
      <div style={{display:'grid',gap:8}}>
        {week.entries.map(e => {
          // Logo on the left, then two stacked lines: client (small,
          // light) above project (large, bold). Non-client rows have no
          // client, so the type label becomes the headline.
          const headline = e.projectName || formatType(e.projectType);
          const company = e.clientName || null;
          return (
            <div key={e.id} style={{
              display:'grid',
              gridTemplateColumns:canEdit ? '1fr auto auto' : '1fr auto',
              gap:8, alignItems:'center', fontSize:12.5,
            }}>
              <div style={{display:'flex',alignItems:'center',gap:10,minWidth:0}}>
                <LogoBadge logoUrl={e.clientLogoUrl} type={e.projectType}/>
                <div style={{minWidth:0}}>
                  {company && (
                    <div style={{fontSize:11,fontWeight:400,color:'var(--fg-muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',lineHeight:1.3}}>{company}</div>
                  )}
                  <div style={{fontSize:15,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',lineHeight:1.3}}>{headline}</div>
                  {e.note && <div style={{fontSize:11,color:'var(--fg-muted)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.note}</div>}
                </div>
              </div>
              <div style={{fontVariantNumeric:'tabular-nums',color:'var(--fg-muted)'}}>{fmtPct(e.percentage)}%</div>
              {canEdit && (
                <button
                  type="button"
                  className="btn-ghost"
                  style={{padding:'2px 8px',fontSize:11,color:'var(--support-red)'}}
                  disabled={e.source === 'holiday-calendar'}
                  title={e.source === 'holiday-calendar' ? 'Public holiday rows are managed automatically.' : 'Delete entry'}
                  onClick={() => onDelete(e.id)}
                >
                  Delete
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Square badge shown at the left of each staffing row: the client logo
// when present, otherwise a colour swatch keyed to the entry type.
const LogoBadge = ({ logoUrl, type }) => {
  if (logoUrl) {
    return <img src={logoUrl} alt="" style={{width:34,height:34,objectFit:'contain',borderRadius:6,flexShrink:0,background:'var(--bg-alt)',border:'1px solid var(--border)'}}/>;
  }
  const colour = TYPE_COLOURS[type] || 'var(--warm-grey-deep)';
  return <div style={{width:34,height:34,borderRadius:6,flexShrink:0,background:colour,opacity:0.85}}/>;
};

const TYPE_COLOURS = {
  'client-project':  'var(--orange)',
  'internal':        'var(--support-purple)',
  'vacation':        'var(--support-green)',
  'public-holiday':  'var(--support-blue-light)',
  'sick':            'var(--support-red)',
};

function formatType(t) {
  switch (t) {
    case 'client-project':  return 'Client project';
    case 'internal':        return 'Internal';
    case 'vacation':        return 'Vacation';
    case 'public-holiday':  return 'Public holiday';
    case 'sick':            return 'Sick';
    default:                return t;
  }
}

function groupByWeek(entries) {
  const map = new Map();
  for (const e of entries) {
    const arr = map.get(e.isoWeek) || [];
    arr.push(e);
    map.set(e.isoWeek, arr);
  }
  return [...map.entries()]
    .sort((a, b) => a[0] < b[0] ? 1 : -1)
    .map(([isoWeek, list]) => ({ isoWeek, entries: list }));
}

function currentIsoWeek() {
  const d = new Date();
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = target.getUTCDay() || 7;
  target.setUTCDate(target.getUTCDate() + 4 - day);
  const year = target.getUTCFullYear();
  const yearStart = Date.UTC(year, 0, 1);
  const week = Math.ceil((((target.getTime() - yearStart) / 86_400_000) + 1) / 7);
  return `${year}-W${String(week).padStart(2, '0')}`;
}

const MembershipList = ({ label, items }) => (
  <div>
    <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:6,fontWeight:600}}>{label}</div>
    {items.length === 0 ? (
      <div style={{fontSize:12.5,color:'var(--fg-muted)'}}>—</div>
    ) : (
      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
        {items.map(name => <span key={name} className="tag" style={{fontSize:11}}>{name}</span>)}
      </div>
    )}
  </div>
);

const Section = ({ label, children }) => (
  <div>
    <div style={{fontSize:11,letterSpacing:'0.08em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:6,fontWeight:600}}>{label}</div>
    {children}
  </div>
);

// Inline uploader for the modal — works for both self (POST /api/me/photo)
// and admin-editing-another (POST /api/admin/users/:id/photo). The Remove
// button immediately deletes server-side; it's not a deferred form field.
export const ModalPhotoUploader = ({ isSelf, targetUserId, currentUrl, onUploaded, onClear }) => {
  const inputRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const onChange = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null); setBusy(true);
    try {
      const result = isSelf
        ? await uploadMyPhoto(file)
        : await uploadAdminUserPhoto(targetUserId, file);
      onUploaded(result.photoUrl);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div style={{display:'flex',alignItems:'center',gap:10}}>
      {currentUrl
        ? <img src={currentUrl} alt="profile" style={{width:48,height:48,borderRadius:'50%',objectFit:'cover',background:'var(--bg-card)'}}/>
        : <div style={{width:48,height:48,borderRadius:'50%',background:'var(--bg-card)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,color:'var(--fg-muted)'}}>no photo</div>}
      <div style={{display:'flex',flexDirection:'column',gap:4,flex:1}}>
        <div style={{display:'flex',gap:6}}>
          <button type="button" className="btn-ghost" onClick={() => inputRef.current?.click()} disabled={busy} style={{padding:'4px 10px',fontSize:11.5}}>
            {busy ? 'Uploading…' : (currentUrl ? 'Replace' : 'Upload')}
          </button>
          {currentUrl && (
            <button type="button" className="btn-ghost" onClick={onClear} disabled={busy} style={{padding:'4px 10px',fontSize:11.5,color:'var(--support-red)'}}>
              Remove
            </button>
          )}
        </div>
        <div style={{fontSize:10.5,color:'var(--fg-muted)'}}>PNG/JPG/WebP/GIF · max 2 MB</div>
        {error && <div style={{fontSize:11,color:'#A20235'}}>{error}</div>}
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{display:'none'}} onChange={onChange}/>
      </div>
    </div>
  );
};
